import { map, filter } from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
// utils
import { axiosInstance as axios } from '../../utils/axios';
//
import { dispatch } from '../store';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  data: [],
  meta: null,
  params: {
    page: 1,
    active: true,
    deleted: false,
  },
  record: null,
};

const slice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET Projects
    getProjectsSuccess(state, action) {
      state.isLoading = false;
      state.data = action.payload.results;
      state.meta = action.payload.meta;
      state.error = false;
    },

    // GET Project
    getProjectSuccess(state, action) {
      state.isLoading = false;
      state.record = action.payload;
      state.error = false;
    },

    // CREATE Project
    createProjectSuccess(state, action) {
      const record = action.payload;
      state.isLoading = false;
      state.data = [...state.data, record];
      state.error = false;
    },

    // UPDATE Project
    updateProjectSuccess(state, action) {
      state.record = action.payload;
      const updateRecord = map(state.data, (_record) => {
        if (_record.id === state.record.id) {
          return state.record;
        }
        return _record;
      });

      state.isLoading = false;
      state.data = updateRecord;
      state.params = { ...state.params };
      state.error = false;
    },

    // DELETE Project
    deleteProjectSuccess(state, action) {
      const { id } = action.payload;
      const deleteRecord = filter(state.data, (user) => user.id !== id);
      state.isLoading = false;
      state.data = deleteRecord;
      state.error = false;
    },

    // CREATE Stage
    createStageSuccess(state, action) {
      const record = action.payload;
      const updatedRecord = state.record;
      updatedRecord.stages = [...updatedRecord.stages, record];
      state.isLoading = false;
      state.record = updatedRecord;
      state.error = false;
    },

    // UPDATE Stage
    updateStageSuccess(state, action) {
      const record = action.payload;
      const updatedRecord = state.record;
      updatedRecord.stages = map(updatedRecord.stages, (_record) => {
        if (_record.id === record.id) {
          return record;
        }
        return _record;
      });
      state.isLoading = false;
      state.record = updatedRecord;
      state.error = false;
    },

    // DELETE Stage
    deleteStageSuccess(state, action) {
      const { id } = action.payload;
      const updatedRecord = state.record;
      updatedRecord.stages = filter(updatedRecord.stages, (_record) => _record.id !== id);
      state.isLoading = false;
      state.record = updatedRecord;
      state.error = false;
    },

    // GET Tasks
    getTasksSuccess(state, action) {
      const updatedRecord = state.record;
      updatedRecord.stages = map(updatedRecord.stages, (_record) => {
        if (_record.id === action.payload.stage) {
          _record.tasks = [..._record.tasks, ...action.payload.results];
        }
        return _record;
      });
      state.isLoading = false;
      state.record = updatedRecord;
      state.error = false;
    },

    // CREATE Task
    createTaskSuccess(state, action) {
      const record = action.payload;
      const updatedRecord = state.record;
      updatedRecord.stages = map(updatedRecord.stages, (_record) => {
        if (_record.id === record.stage) {
          _record.tasks = [..._record.tasks, record];
        }
        return _record;
      });
      state.isLoading = false;
      state.record = updatedRecord;
      state.error = false;
    },

    // UPDATE Task
    updateTaskSuccess(state, action) {
      const record = action.payload;
      const updatedRecord = state.record;
      updatedRecord.stages = map(updatedRecord.stages, (_record) => {
        if (_record.id === record.stage) {
          _record.tasks = map(_record.tasks, (_subrecord) => {
            if (_subrecord.id === record.id) {
              return record;
            }
            return _subrecord;
          });
        }
        return _record;
      });
      state.isLoading = false;
      state.record = updatedRecord;
      state.error = false;
    },

    // DELETE Task
    deleteTaskSuccess(state, action) {
      const { id, stage } = action.payload;
      const updatedRecord = state.record;
      updatedRecord.stages = map(updatedRecord.stages, (_record) => {
        if (_record.id === stage) {
          _record.tasks = filter(_record.tasks, (_subrecord) => _subrecord.id !== id);
        }
        return _record;
      });
      state.isLoading = false;
      state.record = updatedRecord;
      state.error = false;
    },

    // SET PARAMS
    setParams(state, action) {
      state.params = action.payload;
    },

    // REFRESH PARAMS
    refreshParams(state) {
      state.params = { ...state.params };
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const { setParams, refreshParams } = slice.actions;

// ----------------------------------------------------------------------

export function getProjects(params = {}, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/project-board/', {
        params,
        headers: { 'X-Business': businessId },
      });
      dispatch(slice.actions.getProjectsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getProject(id, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`/project-board/${id}/`, { headers: { 'X-Business': businessId } });
      dispatch(slice.actions.getProjectSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function createProject(record, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/project-board/', record, { headers: { 'X-Business': businessId } });
      dispatch(slice.actions.createProjectSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateProject(id, record, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put(`/project-board/${id}/`, record, { headers: { 'X-Business': businessId } });
      dispatch(slice.actions.updateProjectSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function partialUpdateProject(id, record, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.patch(`/project-board/${id}/`, record, { headers: { 'X-Business': businessId } });
      dispatch(slice.actions.updateProjectSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function deleteProject(id, restore = false, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete(`/project-board/${id}/`, { data: { restore }, headers: { 'X-Business': businessId } });
      dispatch(slice.actions.deleteProjectSuccess({ id }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function actionHistoryProject(id, data, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/project-board/${id}/history/`, data, {
        headers: { 'X-Business': businessId },
      });
      dispatch(slice.actions.updateProjectSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// Stage
// ----------------------------------------------------------------------

export function createStage(record, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/project-board/stage/', record, { headers: { 'X-Business': businessId } });
      dispatch(slice.actions.createStageSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateStage(record, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put('/project-board/stage/', record, { headers: { 'X-Business': businessId } });
      dispatch(slice.actions.updateStageSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function deleteStage(id, project, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete('/project-board/stage/', { data: { id, project }, headers: { 'X-Business': businessId } });
      dispatch(slice.actions.deleteStageSuccess({ id }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// Task
// ----------------------------------------------------------------------

export function getTasks(params = {}, businessId) {
  return async () => {
    // dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/project-board/task/', {
        params,
        headers: { 'X-Business': businessId },
      });
      dispatch(slice.actions.getTasksSuccess({ ...response.data, stage: params.stage }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function createTask(record, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/project-board/task/', record, { headers: { 'X-Business': businessId } });
      dispatch(slice.actions.createTaskSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function updateTask(record, businessId) {
  return async () => {
    // dispatch(slice.actions.startLoading());
    try {
      const response = await axios.put('/project-board/task/', record, { headers: { 'X-Business': businessId } });
      dispatch(slice.actions.updateTaskSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function deleteTask(id, stage, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      await axios.delete('/project-board/task/', { data: { id }, headers: { 'X-Business': businessId } });
      dispatch(slice.actions.deleteTaskSuccess({ id, stage }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// ----------------------------------------------------------------------

export function actionHistoryTask(data, businessId) {
  return async () => {
    // dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post(`/project-board/task-history/`, data, {
        headers: { 'X-Business': businessId },
      });
      dispatch(slice.actions.updateTaskSuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

// Board
// ----------------------------------------------------------------------

export function sequenceBoard(data, businessId) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.post('/project-board/sequence/', data, { headers: { 'X-Business': businessId } });
      dispatch(slice.actions.getProjectSuccess(response.data));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}
